import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Grid, Card, CardContent, CardActionArea, Box, Alert
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CancelIcon from '@mui/icons-material/Cancel';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

function StatCard({ title, value, icon, onClick }) {
  return (
    <Card>
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {icon}
            <Box>
              <Typography variant="body2" color="text.secondary">{title}</Typography>
              <Typography variant="h4">{value}</Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const data = await billingApi.get('/api/admin/stats');
      setStats(data.stats);
      setAlerts(data.alerts || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <AdminPageSkeleton />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Billing Admin</Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* System Alerts */}
      {alerts.length > 0 && (
        <Box sx={{ mb: 3 }}>
          {alerts.map((alert, i) => (
            <Alert key={i} severity={alert.severity} sx={{ mb: 1 }}>
              {alert.message}
            </Alert>
          ))}
        </Box>
      )}

      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Subscriptions"
              value={stats.active_subscriptions}
              icon={<PeopleIcon fontSize="large" color="primary" />}
              onClick={() => navigate('/admin/subscriptions')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={`$${(stats.total_revenue_cents / 100).toFixed(0)}`}
              icon={<AttachMoneyIcon fontSize="large" color="success" />}
              onClick={() => navigate('/admin/payments')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="MRR (This Month)"
              value={`$${(stats.mrr_cents / 100).toFixed(0)}`}
              icon={<TrendingUpIcon fontSize="large" color="info" />}
              onClick={() => navigate('/admin/payments')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Canceled (Month)"
              value={stats.canceled_this_month}
              icon={<CancelIcon fontSize="large" color="error" />}
              onClick={() => navigate('/admin/subscriptions?status=canceled')}
            />
          </Grid>
        </Grid>
      )}

      <Typography variant="h6" gutterBottom>By Product</Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {stats?.by_product?.map(p => (
          <Grid item xs={12} sm={6} md={4} key={p.productId}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">{p.product_name}</Typography>
                <Typography variant="h5">{p.count} active</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h6" gutterBottom>Quick Links</Typography>
      <Grid container spacing={2}>
        {[
          { label: 'Pricing', path: '/admin/pricing' },
          { label: 'Pricing History', path: '/admin/pricing/history' },
          { label: 'Bundles', path: '/admin/bundles' },
          { label: 'Financial Ledger', path: '/admin/ledger' },
          { label: 'Subscriptions', path: '/admin/subscriptions' },
          { label: 'Products', path: '/admin/products' },
          { label: 'Discount Codes', path: '/admin/discounts' },
          { label: 'Admin Overrides', path: '/admin/overrides' },
          { label: 'Payments', path: '/admin/payments' },
          { label: 'Audit Log', path: '/admin/audit' }
        ].map(link => (
          <Grid item xs={6} sm={4} md={2} key={link.path}>
            <Card variant="outlined">
              <CardActionArea onClick={() => navigate(link.path)}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography>{link.label}</Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
